<template>
    <div>
        <app-bar :title="t('health_title')">
            <template #actions>
                <div class="px-10">
                    <v-btn
                        :to="{ name: 'articles.create' }"
                        class="sm sm-h"
                        color="orange"
                    >{{ t('health_article_title_add') }}</v-btn>
                </div>
            </template>
        </app-bar>

        <v-layout>
            <v-progress-linear
                v-if="overlay"
                absolute
                indeterminate
            ></v-progress-linear>

            <v-tabs
                class="px-4 my-7"
                height="52"
            >
                <div class="tabs-line"></div>
                <v-tab
                    :to="{ name: 'sections' }"
                    class="pl-0"
                >{{ t('health_tab_sections') }}</v-tab>
                <v-tab
                    :to="{ name: 'articles' }"
                >{{ t('health_tab_articles') }}</v-tab>
                <v-tabs-items>
                    <card-collection
                        class="px-0-nested"
                        :meta="meta"
                        :busy="overlay"
                        @updated="updated"
                    >
                        <template #filters>
                            <v-toolbar
                                elevation="0"
                                color="transparent"
                                class="mt-7"
                                height="56"
                            >
                                <v-text-field
                                    v-model="queries.search"
                                    @click:clear="clear"
                                    class="input-search md mr-2"
                                    clearable
                                    outlined
                                    dense
                                    hide-details
                                    prepend-inner-icon="mdi-magnify"
                                    :label="t('general_label_search')"
                                ></v-text-field>

                                <v-spacer></v-spacer>

                                <v-select
                                    v-model="filters.health_section_id"
                                    :items="sections"
                                    class="md mr-2"
                                    outlined
                                    dense
                                    append-icon="mdi-chevron-down"
                                    hide-details
                                    :label="t('health_article_label_section')"
                                ></v-select>

                                <v-spacer></v-spacer>

                                <v-select
                                    v-model="queries.published"
                                    :items="[ { text: '-', value: '' }, { text: t('general_text_yes'), value: '1' }, { text: t('general_text_no'), value: '0' } ]"
                                    class="md mr-2"
                                    outlined
                                    dense
                                    append-icon="mdi-chevron-down"
                                    hide-details
                                    :label="t('health_article_label_published')"
                                ></v-select>

                                <span class="sub-label px-5">{{ t('general_label_date_period') }}</span>

                                <date-picker-modal
                                    :value="queries.date_from"
                                    @updated="(v) => queries.date_from = v"
                                >
                                    <template #default="{ on, attrs }">
                                        <div
                                            v-on="on"
                                            v-bind="attrs"
                                        >
                                            <v-text-field
                                                v-model="queries.date_from"
                                                class="xs input-color-inherit"
                                                outlined
                                                dense
                                                hide-details
                                                readonly
                                                :label="t('general_label_from')"
                                            ></v-text-field>
                                        </div>
                                    </template>
                                </date-picker-modal>

                                <span class="sub-label px-2">-</span>

                                <date-picker-modal
                                    :value="queries.date_to"
                                    @updated="(v) => queries.date_to = v"
                                >
                                    <template #default="{ on, attrs }">
                                        <div
                                            v-on="on"
                                            v-bind="attrs"
                                        >
                                            <v-text-field
                                                v-model="queries.date_to"
                                                class="xs input-color-inherit"
                                                outlined
                                                dense
                                                hide-details
                                                readonly
                                                :label="t('general_label_to')"
                                            ></v-text-field>
                                        </div>
                                    </template>
                                </date-picker-modal>

                                <v-spacer></v-spacer>

                                <v-btn
                                    @click="search"
                                    class="sm mx-5"
                                    color="primary"
                                >{{ t('general_action_search') }}</v-btn>

                                <v-spacer></v-spacer>

                                <v-btn
                                    v-ripple="false"
                                    plain
                                    color="orange darken-4"
                                    @click="reset"
                                >{{ t('general_action_reset_filters') }}</v-btn>

                            </v-toolbar>
                        </template>

                        <template #data>
                            <thead>
                            <tr>
                                <th class="touched">
                                    <sort-field
                                        v-model="queries.sort"
                                        field="id"
                                        @touched="onSort"
                                    >{{ t('health_article_label_id') }}
                                    </sort-field>
                                </th>
                                <th>
                                    <sort-field
                                        v-model="queries.sort"
                                        field="name"
                                        @touched="onSort"
                                    >{{ t('health_article_label_name') }}
                                    </sort-field>
                                </th>
                                <th>
                                    <sort-field
                                        v-model="queries.sort"
                                        field="created_at"
                                        @touched="onSort"
                                    >{{ t('general_label_creation_date') }}
                                    </sort-field>
                                </th>
                                <th>
                                    <sort-field
                                        v-model="queries.sort"
                                        field="published_at"
                                        @touched="onSort"
                                    >{{ t('general_label_publishing_date') }}
                                    </sort-field>
                                </th>
                                <th>{{ t('health_article_label_section') }}</th>
                                <th>
                                    <sort-field
                                        v-model="queries.sort"
                                        field="published"
                                        @touched="onSort"
                                    >{{ t('health_article_label_published') }}
                                    </sort-field>
                                </th>
                                <th class="text-center">{{ t('general_label_action') }}</th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-if="collection.data.length === 0">
                                <td class="text-center" colspan="7">{{ t('general_text_no_data') }}</td>
                            </tr>
                            <template v-else v-for="article in collection.data">
                                <tr>
                                    <td>
                                        <a
                                            :href="`/health/articles/${article.id}/view`"
                                            @click.prevent="touch(article)"
                                            :class="{ 'orange--text': ! article.published }"
                                        >{{ article.id }}</a>
                                    </td>
                                    <td>
                                        <a
                                            :href="`/health/articles/${article.id}/view`"
                                            @click.prevent="touch(article)"
                                        >{{ article.name }}</a>
                                    </td>
                                    <td>
                                        {{ article.createdAt.format('DD/MM/YYYY') }}
                                    </td>
                                    <td>
                                        {{ article.publishedAt.format('DD/MM/YYYY HH:mm') }}
                                    </td>
                                    <td>{{ article.section.name }}</td>
                                    <td>
                                        {{ article.published ? t('general_text_yes') : t('general_text_no') }}
                                    </td>
                                    <td class="text-center">
                                        <v-menu
                                            offset-y
                                            left
                                        >
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-btn
                                                    icon
                                                    v-bind="attrs"
                                                    v-on="on"
                                                >
                                                    <v-icon>mdi-dots-vertical</v-icon>
                                                </v-btn>
                                            </template>
                                            <v-list dense>
                                                <v-list-item
                                                    @click.prevent="edit(article)"
                                                >
                                                    <v-list-item-title
                                                        class="customized-deep-primary--text"
                                                    >{{ t('general_action_edit') }}
                                                    </v-list-item-title>
                                                </v-list-item>
                                                <v-list-item
                                                    @click.prevent="suspend(article)"
                                                >
                                                    <v-list-item-title
                                                        v-if="article.published"
                                                        class="customized-deep-primary--text"
                                                    >{{ t('general_action_suspend') }}
                                                    </v-list-item-title>
                                                    <v-list-item-title
                                                        v-else
                                                        class="orange--text"
                                                    >{{ t('general_action_unsuspend') }}
                                                    </v-list-item-title>
                                                </v-list-item>
                                                <confirm-modal
                                                    :title="t('general_title_confirm_action')"
                                                    @agree="del(article)"
                                                >
                                                    <template #default="{ on, attrs }">
                                                        <v-list-item
                                                            href
                                                            v-on="on"
                                                            v-bind="attrs"
                                                        >
                                                            <v-list-item-title
                                                                class="customized-deep-primary--text"
                                                            >{{ t('general_action_delete') }}</v-list-item-title>
                                                        </v-list-item>
                                                    </template>
                                                </confirm-modal>
                                            </v-list>
                                        </v-menu>
                                    </td>
                                </tr>
                            </template>
                            </tbody>
                        </template>
                    </card-collection>
                </v-tabs-items>
            </v-tabs>
        </v-layout>
    </div>
</template>

<script lang="ts">
    import { computed, defineComponent, onMounted, reactive, toRefs, watch } from '@vue/composition-api'
    import { useI18n } from 'vue-i18n-composable'
    import { useStore } from '@/store'
    import { useRouter } from '@/composition-api'
    import { HealthArticleCollection } from '@/store/modules/health-article-collection'
    import { HealthArticle } from '@/core/models/health-article'
    import { HealthSection } from '@/core/models/health-section'
    import DatePickerModal from '@/components/DatePickerModal.vue'
    import CardCollection from '@/components/CardCollection.vue'
    import ConfirmModal from '@/components/ConfirmModal.vue'
    import SortField from '@/components/SortField.vue'

    export default defineComponent({

        components: {
            DatePickerModal,
            CardCollection,
            ConfirmModal,
            SortField,
        },

        /**
         * Component constructor.
         * @param props
         */
        setup(props) {
            const state = reactive({
                overlay: true,
                meta: undefined,
            })

            const filters = reactive({
                health_section_id: undefined,
            })

            const queries = reactive({
                search: undefined,
                page: 1,
                per_page: 20,
                sort: '-id',
                published: '',
                date_from: undefined,
                date_to: undefined,
                relations: 'section',
                filters: <any>{}
            })

            const reset = () => {
                filters.health_section_id = undefined
                queries.search = undefined
                queries.date_from = undefined
                queries.published = ''
                queries.date_to = undefined
                queries.filters = <any>{}
                capture()
            }

            const store = useStore()
            const router = useRouter()

            const collection = computed(() => <HealthArticleCollection>store.getters['healthArticleCollection/get'])
            const sections = computed(
                () => [{ text: '-', value: '' }].concat(
                    store.getters['healthSectionCollection/get'].data.map((item: HealthSection) => {
                        return { text: item.name, value: item.id.toString() }
                    })
                )
            )

            const updated = (updatedQueries: any) => {
                queries.page = updatedQueries.page
                capture()
            }

            const capture = () => {
                state.overlay = true
                store.dispatch('healthArticleCollection/get', queries).then((data) => {
                    state.meta = data.meta
                    state.overlay = false
                })
            }

            const touch = (article: HealthArticle) => {
                router.push({ name: 'articles.view', params: <any>{ id: article.id } })
            }

            const edit = (article: HealthArticle) => {
                router.push({ name: 'articles.show', params: <any>{ id: article.id } })
            }

            const suspend = (article: HealthArticle) => {
                state.overlay = true
                store.dispatch('healthArticle/update', {
                    id: article.id,
                    payload: { published: ! article.published }
                }).then(() => {
                    capture()
                })
            }

            const del = (article: HealthArticle) => {
                state.overlay = true
                store.dispatch('healthArticle/delete', { id: article.id }).then(() => {
                    capture()
                })
            }

            const search = () => {
                queries.filters = filters
                capture()
            }

            const clear = () => {
                queries.search = undefined
                capture()
            }

            const onSort = (value: any) => {
                if (value === undefined) {
                    value = '-id'
                }
                queries.sort = value
            }

            watch(() => [
                queries.sort,
                queries.published,
                filters.health_section_id,
            ], () => {
                queries.filters = filters
                capture()
            })

            onMounted(() => {
                collection.value.data = []
                store.dispatch('healthSectionCollection/get', { sort: '-id' })
                capture()
            })

            return {
                queries,
                filters,
                collection: collection.value,
                sections,
                updated,
                touch,
                edit,
                suspend,
                del,
                onSort,
                search,
                clear,
                reset,
                ...useI18n(),
                ...toRefs(state)
            }
        }
    })
</script>
